import type { Pinia } from 'pinia';
import { createRouter, createWebHistory, type RouteRecordRaw, type Router } from 'vue-router';
import { HOME_PATH, LOGIN_PATH, TABS, guardDecision } from './navigation';
import { SETTINGS_HOME, SETTINGS_SECTIONS, settingsPath, settingsRouteName } from './reglages';
import { useAuthStore } from './stores/auth';
import { useNouveautesStore } from './stores/nouveautes';
import AppLayout from './views/AppLayout.vue';
import LoginView from './views/LoginView.vue';

declare module 'vue-router' {
  interface RouteMeta {
    public?: boolean;
    adminOnly?: boolean;
    title?: string;
    /** Onglet à mettre en avant pour une page hors onglets (ex. Première configuration → Réglages). */
    tab?: string;
  }
}

const VIEWS: Record<string, () => Promise<unknown>> = {
  afaire: () => import('./views/AFaire.vue'),
  support: () => import('./views/Support.vue'),
  projets: () => import('./views/Projets.vue'),
  conseiller: () => import('./views/Conseiller.vue'),
  chiffres: () => import('./views/Chiffres.vue'),
  tresorerie: () => import('./views/Tresorerie.vue'),
  depenses: () => import('./views/Depenses.vue'),
  carte: () => import('./views/Carte.vue'),
  agenda: () => import('./views/Agenda.vue'),
  apporteurs: () => import('./views/Apporteurs.vue'),
  retours: () => import('./views/Retours.vue'),
  reglages: () => import('./views/Reglages.vue'),
};

export const routes: RouteRecordRaw[] = [
  { path: LOGIN_PATH, name: 'login', component: LoginView, meta: { public: true, title: 'Connexion' } },
  {
    path: '/',
    component: AppLayout,
    children: [
      { path: '', redirect: HOME_PATH },
      ...TABS.map((tab): RouteRecordRaw => ({
        path: tab.path.slice(1),
        name: tab.name,
        component: VIEWS[tab.name] ?? VIEWS.afaire!,
        meta: { adminOnly: tab.adminOnly ?? false, title: tab.label },
      })),
      // Lot BB : vues Fournisseurs et « Ce que je sais » de l'onglet Support (même écran, barre de vues).
      {
        path: 'support/fournisseurs',
        name: 'support-fournisseurs',
        component: VIEWS.support!,
        meta: { title: 'Fournisseurs · Support', tab: 'support' },
      },
      {
        path: 'support/memoire',
        name: 'support-memoire',
        component: VIEWS.support!,
        meta: { title: 'Ce que je sais · Support', tab: 'support' },
      },
      // Lot BL : simulations (Chiffres > « Simuler → », Projets > plusieurs devis), onglet de départ en avant.
      {
        path: 'chiffres/simuler',
        name: 'chiffres-simuler',
        component: () => import('./views/ChiffresSimulation.vue'),
        meta: { title: 'Simuler · Chiffres', tab: 'chiffres' },
      },
      {
        path: 'projets/simulation',
        name: 'projets-simulation',
        component: () => import('./views/ProjetsSimulation.vue'),
        meta: { title: 'Simulation · Projets', tab: 'projets' },
      },
      {
        // Pour chaque membre (Réglages est réservé à l'administrateur) : appli iPhone, push, préférences.
        path: 'notifications',
        name: 'notifications',
        component: () => import('./views/Notifications.vue'),
        meta: { title: 'Notifications' },
      },
      {
        // Lot AV : page d'un client (Carte, projets, cartes, volet R1, recherche de l'en-tête).
        path: 'clients/:id',
        name: 'client',
        component: () => import('./views/Client.vue'),
        meta: { title: 'Client' },
      },
      {
        // Lot AY : fiche d'un sous-traitant (recherche de l'en-tête, Réglages > Chiffres).
        path: 'sous-traitants/:id',
        name: 'subcontractor',
        component: () => import('./views/SousTraitant.vue'),
        meta: { title: 'Sous-traitant' },
      },
      {
        // Lot BA : toutes les nouveautés, version par version (pied de page, cloche, « Quoi de neuf »).
        path: 'nouveautes',
        name: 'nouveautes',
        component: () => import('./views/Nouveautes.vue'),
        meta: { title: 'Nouveautés' },
      },
      {
        path: 'compte-google',
        name: 'google-account',
        component: () => import('./views/GoogleAccount.vue'),
        meta: { title: 'Mon compte Google' },
      },
      // Lot AH [29] : une adresse par section des Réglages (`/reglages` = Accueil, route de l'onglet).
      ...SETTINGS_SECTIONS.filter((s) => s.slug !== SETTINGS_HOME).map((section): RouteRecordRaw => ({
        path: settingsPath(section.slug).slice(1),
        name: settingsRouteName(section.slug),
        component: VIEWS.reglages!,
        meta: { adminOnly: true, title: `${section.label} · Réglages`, tab: 'reglages' },
      })),
    ],
  },
  // Mode écran (lot E2) : plein cadre, hors de la coquille (ni en-tête ni onglets).
  {
    path: '/ecran',
    name: 'ecran',
    component: () => import('./views/Ecran.vue'),
    meta: { title: 'Mode écran' },
  },
  { path: '/:pathMatch(.*)*', redirect: HOME_PATH },
];

export function createAppRouter(pinia: Pinia): Router {
  const router = createRouter({
    history: createWebHistory(),
    routes,
    // Liens de la Première configuration (`/reglages/connexions#connexion-axonaut`) : défiler jusqu'à la carte.
    scrollBehavior(to, _from, saved) {
      if (saved) return saved;
      // Les cartes des Réglages se chargent après la navigation : on leur laisse un instant.
      if (to.hash)
        return new Promise((resolve) =>
          setTimeout(
            () => resolve(document.querySelector(to.hash) ? { el: to.hash, top: 72 } : { top: 0 }),
            400,
          ),
        );
      return { top: 0 };
    },
  });

  router.beforeEach(async (to) => {
    const auth = useAuthStore(pinia);
    await auth.init();
    const decision = guardDecision(
      {
        path: to.path,
        fullPath: to.fullPath,
        meta: { public: to.meta.public, adminOnly: to.meta.adminOnly },
      },
      auth.me,
    );
    return decision === true ? true : decision;
  });

  router.afterEach((to, from) => {
    // Lot BA : ouvrir un onglet (pas le premier écran affiché) éteint son point vert « nouveautés ».
    if (from.matched.length > 0) useNouveautesStore(pinia).visitTab(String(to.meta.tab ?? to.name ?? ''));
    document.title = to.meta.title ? `${to.meta.title} · Cockpit Evolusite` : 'Cockpit Evolusite';
  });
  return router;
}
